import { SxProps, Theme } from "@mui/material";

export type LiquidGlassColors = {
  fill: string;
  hoverFill: string;
  /** Slightly darker (light mode) / lighter (dark mode) for selected states. */
  selectedFill: string;
  foreground: string;
  border: string;
};

/** Derive glass fill / foreground from navigation bar background darkness. */
export function getLiquidGlassColors(navBackground: string): LiquidGlassColors {
  const isDark = isColorDark(navBackground);
  if (isDark) {
    return {
      fill: "rgba(40, 40, 40, 0.72)",
      hoverFill: "rgba(55, 55, 55, 0.85)",
      selectedFill: "rgba(90, 90, 90, 0.88)",
      foreground: "#FFFFFF",
      border: "rgba(255, 255, 255, 0.16)",
    };
  }
  return {
    fill: "rgba(255, 255, 255, 0.72)",
    hoverFill: "rgba(255, 255, 255, 0.85)",
    selectedFill: "rgba(230, 230, 230, 0.88)",
    foreground: "#000000",
    border: "rgba(0, 0, 0, 0.08)",
  };
}

/** Shared frosted-glass capsule styles for the iOS liquid glass preview. */
export function liquidGlassCapsuleSx(
  colors: LiquidGlassColors,
  options?: {
    borderRadius?: string | number;
    padding?: string | number;
    selected?: boolean;
  }
): SxProps<Theme> {
  return {
    backgroundColor: options?.selected ? colors.selectedFill : colors.fill,
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    border: `0.5px solid ${colors.border}`,
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
    borderRadius: options?.borderRadius ?? "999px",
    padding: options?.padding,
    color: colors.foreground,
  };
}

function isColorDark(color: string): boolean {
  const rgb = parseRgb(color);
  if (!rgb) {
    return false;
  }
  // Relative luminance (sRGB)
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance < 0.5;
}

function parseRgb(color: string): [number, number, number] | null {
  if (color.startsWith("#")) {
    const hex =
      color.length === 4
        ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
        : color;
    if (hex.length !== 7) {
      return null;
    }
    return [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
  }

  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) {
    return null;
  }
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}
